// src/features/crypto-info/components/CryptoIntradayChart.tsx
//
// Component Island: intraday price momentum area chart. Fetches hourly OHLCV
// bars from GET /api/v1/crypto-info/{symbol}/chart?interval=1hour and plots the
// close. FMP returns bars newest-first; Recharts plots left-to-right
// chronologically, so we reverse() a shallow copy before charting.
//
// FMP crypto timestamps arrive as "YYYY-MM-DD HH:MM:SS" (space-separated, not
// ISO-T), which Safari/strict parsers reject via `new Date(...)`. We therefore
// derive the HH:MM tick by slicing the string rather than constructing a Date.

import { useEffect, useState } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { fetchCryptoChart, type ChartBar } from '../api';

// "2026-07-17 02:00:00" -> "02:00"; tolerant of ISO-T too.
const formatTimeTick = (v: string): string => {
    if (!v) return '';
    const timePart = v.includes(' ') ? v.split(' ')[1] : v.split('T')[1] ?? '';
    return timePart ? timePart.slice(0, 5) : v.slice(0, 10);
};

const formatUsd = (v: number): string =>
    `$${v.toLocaleString(undefined, { maximumFractionDigits: v < 1 ? 6 : 2 })}`;

export const CryptoIntradayChart = ({ symbol }: { symbol: string }) => {
    const [data, setData] = useState<ChartBar[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        fetchCryptoChart(symbol, '1hour')
            .then((res) => {
                // FMP is newest-first; reverse to oldest-first for the chart.
                if (active) setData([...res].reverse());
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (active) setData([]);
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [symbol]);

    if (isLoading)
        return (
            <div className="h-[440px] animate-pulse bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700" />
        );
    if (!data.length)
        return (
            <div className="h-[440px] flex items-center justify-center text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                Chart unavailable
            </div>
        );

    const closes = data.map((d) => d.close);
    const minPrice = Math.min(...closes);
    const maxPrice = Math.max(...closes);

    return (
        <div className="h-[440px] w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
            <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">
                Intraday Momentum (1H)
            </h3>
            <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={data}>
                    <defs>
                        <linearGradient
                            id="cryptoColorOrange"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="5%"
                                stopColor="#f97316"
                                stopOpacity={0.3}
                            />
                            <stop
                                offset="95%"
                                stopColor="#f97316"
                                stopOpacity={0}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#334155"
                        opacity={0.2}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatTimeTick}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        minTickGap={30}
                    />
                    <YAxis
                        domain={[minPrice * 0.99, maxPrice * 1.01]}
                        tickFormatter={formatUsd}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={80}
                        orientation="right"
                    />
                    <Tooltip
                        contentStyle={{
                            borderRadius: '8px',
                            backgroundColor: '#1e293b',
                            border: 'none',
                            color: '#f8fafc',
                        }}
                        labelFormatter={(v: string) => v}
                        formatter={(v: number) => [formatUsd(v), 'Price']}
                    />
                    <Area
                        type="monotone"
                        dataKey="close"
                        stroke="#f97316"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#cryptoColorOrange)"
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CryptoIntradayChart;
